import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  FileText, Upload, Download, Trash2, Lock, Zap, Loader2, Paperclip,
} from "lucide-react";

const KINDS = [
  { key: "resume", label: "Resume", icon: FileText },
  { key: "cover_letter", label: "Cover letter", icon: Paperclip },
];

function fmtBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentsPanel({ applicationId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.tier === "pro";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // kind currently uploading
  const refs = { resume: useRef(null), cover_letter: useRef(null) };

  const load = async () => {
    if (!applicationId || !isPro) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/applications/${applicationId}/documents`);
      setDocs(data);
    } catch (err) {
      if (err.response?.status !== 402) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [applicationId, isPro]);

  const findDoc = (kind) => docs.find((d) => d.kind === kind);

  const handlePick = (kind) => {
    if (!isPro) return;
    refs[kind].current?.click();
  };

  const handleUpload = async (kind, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File is too large (max 5 MB).");
      e.target.value = "";
      return;
    }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "docx"].includes(ext)) {
      toast.error("Only PDF or DOCX files are supported.");
      e.target.value = "";
      return;
    }
    setUploading(kind);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(
        `/applications/${applicationId}/documents?kind=${kind}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${kind === "resume" ? "Resume" : "Cover letter"} uploaded.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || `${doc.kind}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleDelete = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Removed.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!applicationId) {
    return (
      <div className="rounded-xl border border-dashed border-[#D4CFC5] bg-[#F4F1EE]/50 p-4 text-sm text-[#5C6B64]" data-testid="docs-panel-empty-app">
        Save this application first to attach documents.
      </div>
    );
  }

  if (!isPro) {
    return (
      <div
        className="rounded-xl bg-[#1A362D] text-[#F4F1EE] p-4 flex items-start gap-3"
        data-testid="docs-panel-paywall"
      >
        <Lock className="w-4 h-4 text-[#D99C3D] shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium">Attach a resume & cover letter</p>
          <p className="text-sm opacity-80 mt-0.5">
            Keep the exact version you sent to each company — a Trackr Pro feature.
          </p>
        </div>
        <Button
          onClick={() => navigate("/upgrade")}
          className="rounded-full bg-[#D99C3D] hover:bg-[#C48A2E] text-[#1A362D] font-semibold shrink-0"
          data-testid="docs-panel-upgrade-btn"
        >
          <Zap className="w-3.5 h-3.5 mr-1" /> Upgrade
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="docs-panel">
      {KINDS.map(({ key, label, icon: Icon }) => {
        const doc = findDoc(key);
        const isUploading = uploading === key;
        return (
          <div
            key={key}
            className="rounded-xl bg-white border border-[#F0EDE7] p-3 flex items-center gap-3"
            data-testid={`docs-row-${key}`}
          >
            <div className="w-9 h-9 rounded-lg bg-[#F4F1EE] flex items-center justify-center shrink-0">
              <Icon className="w-4 h-4 text-[#1A362D]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1A362D]">{label}</p>
              {loading ? (
                <p className="text-xs text-[#5C6B64]">Loading…</p>
              ) : doc ? (
                <p className="text-xs text-[#5C6B64] truncate" title={doc.original_filename}>
                  {doc.original_filename} · {fmtBytes(doc.size)}
                </p>
              ) : (
                <p className="text-xs text-[#5C6B64]">Not attached · PDF or DOCX up to 5 MB</p>
              )}
            </div>
            <input
              ref={refs[key]}
              type="file"
              accept=".pdf,.docx"
              className="hidden"
              onChange={(e) => handleUpload(key, e)}
              data-testid={`docs-file-input-${key}`}
            />
            {doc ? (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownload(doc)}
                  className="h-8 w-8 rounded-full"
                  title="Download"
                  data-testid={`docs-download-${key}`}
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handlePick(key)}
                  className="h-8 w-8 rounded-full"
                  title="Replace"
                  disabled={isUploading}
                  data-testid={`docs-replace-${key}`}
                >
                  {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(doc)}
                  className="h-8 w-8 rounded-full text-[#C65D47] hover:bg-[#FCECEC]"
                  title="Remove"
                  data-testid={`docs-delete-${key}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePick(key)}
                disabled={isUploading}
                className="rounded-full shrink-0"
                data-testid={`docs-attach-${key}`}
              >
                {isUploading ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>
                ) : (
                  <><Upload className="w-3.5 h-3.5 mr-1.5" /> Attach</>
                )}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
